import { useForm } from '@abyss/web/hooks/useForm';
import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Card } from '@abyss/web/ui/Card';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { ProgressBar } from '@abyss/web/ui/ProgressBar';
import { Text } from '@abyss/web/ui/Text';
import { ProviderDetails } from '#/models/ProviderDetails';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { mobileOnly } from '../../../../common/ConstantsStyles';
import {
  OnboardingForm,
  PreferencesForm,
} from '../../../../common/PSXHeader/SmartChoice';
import { RecommendationProp } from '../../../../common/PSXHeader/SmartChoice/PreferencesForm';
import {
  countPreferences,
  formatPreferenceData,
  formatPreferencePayload,
} from '../../../../common/PSXHeader/SmartChoice/utils';
import { SmartChoiceBreakDownL1 } from '../../../../common/SmartChoice/SmartChoiceBreakDownL1';
import { BreakdownDrawer } from '../../../../common/SmartChoiceBreakDownDrawer/BreakdownDrawer';
import { getFeatureFlag } from '../../../../common/Utils';
import { useHsidUuid } from '../../../../hooks/useHsidUuid';
import { useLagoon } from '../../../../hooks/useLagoon';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import {
  RecommendationCriteriaMatch,
  RecommendationScoreMatch,
  breakDownKeys,
  getChipTextColor,
  getChipbackgroundColor,
  getMatchDescription,
} from '../../../../utils/smartChoice.utils';
import { ContentWrapper } from '../../../ContentWrapper';
import {
  CardContainer,
  CardSubContainer,
  ContentContainer,
  ContentTitle,
  ContentValue,
  CriteriaContentContainer,
  PercentageBox,
  PercentageText,
  RightContainer,
  ViewMoreText,
} from './SmartChoice.style';

type Props = {
  recommendationDetails: object;
  locationKey: string;
  handleTabChange: (val: number) => void;
  providerDetails: {
    providerName: string;
    primaryDegrees: string[];
    specialties: string[];
  };
  preferencesData: RecommendationProp;
  saveRecommendationPreferences: (val: any) => void;
  setPreferencesModalOpen: ({}) => void;
  isPreferencesModalOpen: boolean;
  isDetailsWidget?: boolean;
};

export const SmartChoice = ({
  recommendationDetails,
  locationKey,
  providerDetails,
  preferencesData,
  handleTabChange,
  saveRecommendationPreferences,
  isPreferencesModalOpen = false,
  setPreferencesModalOpen,
  isDetailsWidget = false,
}: Props) => {
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const { t } = useTranslation();
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const mobileScreen = useMediaQuery(mobileOnly) || isWidget;
  const [preferenceCount, setPreferenceCount] = useState(0);

  const ENABLE_SMART_CHOICE_UPDATED_PROVIDER_INFO: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SMART_CHOICE_UPDATED_PROVIDER_INFO
  );

  const [smarChoiceBreakDownL1, setSmarChoiceBreakDownL1] = useState(false);
  const [smartChoiceBreakDownL3, setSmartChoiceBreakDownL3] = useState(false);
  const [drawerType, setDrawerType] = useState('');
  const [fromL2DrawerToHaveBackButton, setFromL2DrawerToHaveBackButton] =
    useState(false);

  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [coachmarkWeb, setCoachmarkWebOpen] = useState<boolean>(false);
  const hsidUuid = useHsidUuid();
  const toggleDrawer = (str) => {
    setFromL2DrawerToHaveBackButton(false);
    setSmartChoiceBreakDownL3(true);
    setDrawerType(str);
  };
  const onBreakDownClick = (str) => {
    setSmarChoiceBreakDownL1(false);
    setSmartChoiceBreakDownL3(true);
    setDrawerType(str);
    setFromL2DrawerToHaveBackButton(true);
  };

  const form = useForm({
    values: formatPreferenceData(preferencesData),
  });
  const formValues = useMemo(() => form.getValues(), [form]);

  useEffect(() => {
    if (formValues) {
      const formFields = JSON.parse(JSON.stringify(form.getValues()));
      const enabledPreferences = countPreferences(formFields);
      setPreferenceCount(enabledPreferences);
    }
  }, [formValues]);

  const onClickSmartChoiceHandler = () => {
    setSmarChoiceBreakDownL1(false);
    if (preferencesData?.hasSetPreferences === 'y') {
      setPreferencesModalOpen(true);
    } else {
      if (preferencesData?.hasDismissedCoachMarkWeb !== 'y') {
        updatePreferencesForFirstTimeUser();
      }
      setShowOnboardingForm(true);
    }
  };

  const renderBreakDownCard = (key: string): JSX.Element => {
    let match =
      recommendationDetails?.[key]?.scoreDetails?.match ??
      RecommendationScoreMatch.Low;

    if (key === 'quality') {
      match =
        recommendationDetails?.[key]?.scoreDetails?.match ??
        RecommendationCriteriaMatch.None;
    }
    return (
      <Card
        css={{
          marginTop: '16px !important',
          padding: '0px !important',
          marginRight: '8px',
          cursor: 'pointer',
          width: 'auto',
        }}
        data-testid="smart-choice-card"
        key={key}
        onClick={() => {
          adobeLinkTrackEvent({
            name: breakDownKeys[key],
            location: `Smart choice ${breakDownKeys[key]} breakdown`,
          });
          toggleDrawer(key);
        }}
      >
        <ContentContainer>
          <ContentTitle
            color="$gray8"
            data-testid={`smart-choice-card-title-${key}`}
          >
            {t(breakDownKeys[key])}:
          </ContentTitle>
          <CriteriaContentContainer>
            <ContentValue
              css={{
                backgroundColor: getChipbackgroundColor(match, key),
                color: getChipTextColor(match, key),
              }}
              data-testid={`smart-choice-card-value-${key}`}
            >
              {t(getMatchDescription(match, key))}
            </ContentValue>
          </CriteriaContentContainer>
          <ProgressBar
            barColor="$gray3"
            css={{ borderWidth: '0px' }}
            height={'8px'}
            percentage={
              recommendationDetails?.[key]?.scoreDetails?.percentage ?? 0
            }
          />
        </ContentContainer>
      </Card>
    );
  };

  const renderBreakDownData = (): JSX.Element[] => {
    const breakDownData: JSX.Element[] = [];
    Object.keys(breakDownKeys)
      .filter(
        (item) =>
          !(
            item === 'verification' &&
            !ENABLE_SMART_CHOICE_UPDATED_PROVIDER_INFO
          )
      )
      ?.map((key) => breakDownData.push(renderBreakDownCard(key)));
    return breakDownData;
  };

  const recommendationScore = (
    recommendationDetails as { totalRecommendationScore?: number }
  )?.totalRecommendationScore;

  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;

  const onClickSaveAndUpdate = (data) => {
    saveRecommendationPreferences({
      variables: { ...data, ...(hsidUuid && { userId: hsidUuid }) },
    });
  };

  const updatePreferencesForFirstTimeUser = () => {
    const payload = formatPreferencePayload({
      ...preferencesData,
      hasDismissedCoachMarkWeb: 'y',
      isSuperUser,
    });
    onClickSaveAndUpdate(payload);
  };

  const onCloseCoachmarkWeb = () => {
    updatePreferencesForFirstTimeUser();
    setCoachmarkWebOpen(false);
  };

  return (
    <>
      <CardContainer>
        <ContentWrapper isWidget={false}>
          <CardSubContainer cssProps={{ isDetailsWidget }}>
            <Flex
              alignItems={mobileScreen ? 'center' : 'baseline'}
              css={{
                flexWrap: 'nowrap',
              }}
            >
              <PercentageBox>
                <PercentageText
                  color="$white"
                  data-testid="smart-choice-percentage-text"
                >
                  {recommendationScore}%
                </PercentageText>
              </PercentageBox>

              <RightContainer>
                <Flex
                  alignItems="center"
                  css={{ marginBottom: mobileScreen ? '0px' : '16px' }}
                  justify="space-between"
                >
                  <Flex
                    alignItems={mobileScreen ? 'flex-start' : 'center'}
                    css={{ flexDirection: mobileScreen ? 'column' : 'row' }}
                  >
                    <Text
                      className="smart-choice-text"
                      css={{
                        'abyss-text-root': {
                          fontSize: mobileScreen ? '20px' : '25px',
                        },
                      }}
                      data-testid="smart-choice-text"
                    >
                      {t('SMART_CHOICE.SMART_CHOICE')}
                    </Text>

                    <Text
                      className="desc-text"
                      color="$gray7"
                      data-testid="smart-choice-preference-text"
                    >
                      <span
                        data-testid="smart-choice-preference-count"
                        style={{ fontWeight: mobileScreen ? 500 : 700 }}
                      >
                        {preferenceCount}
                      </span>{' '}
                      {mobileScreen
                        ? t('SMART_CHOICE.PREFERENCES_APPLIED')
                        : t('SMART_CHOICE.PERSONAL_PREFERENCES_APPLIED')}
                    </Text>
                  </Flex>
                  <Flex
                    css={{ cursor: 'pointer' }}
                    data-testid="smart-choice-view-more-btn"
                    onClick={() => {
                      adobeLinkTrackEvent({
                        name: 'View more',
                        location: 'Smart choice',
                      });
                      setSmarChoiceBreakDownL1(true);
                    }}
                  >
                    {!mobileScreen && (
                      <ViewMoreText data-testid="smart-choice-view-more-text">
                        {t('SMART_CHOICE.VIEW_MORE')}
                      </ViewMoreText>
                    )}
                    <IconMaterial color="black" icon="chevron_right" />
                  </Flex>
                </Flex>

                {!mobileScreen && <Divider height={0.5} />}
              </RightContainer>
            </Flex>
            <Flex>{!mobileScreen && renderBreakDownData()}</Flex>
          </CardSubContainer>
        </ContentWrapper>
      </CardContainer>
      {smarChoiceBreakDownL1 && (
        <SmartChoiceBreakDownL1
          coachmarkWeb={coachmarkWeb}
          handleCloseDrawer={() => setSmarChoiceBreakDownL1(false)}
          isOpen={smarChoiceBreakDownL1}
          onBreakDownClick={onBreakDownClick}
          onClickSmartChoiceHandler={onClickSmartChoiceHandler}
          onCloseCoachmarkWeb={() => onCloseCoachmarkWeb()}
          preferencesData={preferencesData}
          providerDetails={providerDetails}
          recommendationDetails={recommendationDetails}
          setCoachmarkWebOpen={setCoachmarkWebOpen}
        />
      )}
      {smartChoiceBreakDownL3 && (
        <BreakdownDrawer
          drawerType={drawerType}
          handleTabChange={handleTabChange}
          haveBackButton={fromL2DrawerToHaveBackButton}
          isOpen={smartChoiceBreakDownL3}
          locationKey={locationKey}
          preferencesData={preferencesData}
          providerDetails={providerDetails as ProviderDetails}
          recommendationDetails={recommendationDetails}
          setFromL2DrawerToHaveBackButton={() =>
            setFromL2DrawerToHaveBackButton(false)
          }
          setIsOpen={() => setSmartChoiceBreakDownL3(false)}
          setSmarChoiceBreakDownL1={setSmarChoiceBreakDownL1}
        />
      )}
      {showOnboardingForm && (
        <OnboardingForm
          handleCloseDrawer={() => setShowOnboardingForm(false)}
          handleOpenSetPreferencesModal={() => setPreferencesModalOpen(true)}
          isOpen={showOnboardingForm}
        />
      )}
      {isPreferencesModalOpen && (
        <PreferencesForm
          handleCloseDrawer={() => setPreferencesModalOpen(false)}
          isOpen={isPreferencesModalOpen}
          onClickSaveAndUpdate={onClickSaveAndUpdate}
          preferencesData={preferencesData}
          preferencesFormFromProviderDetail={true}
        />
      )}
    </>
  );
};
